import { EIRData } from '../types';

function parseKeyValueLine(line: string): { key: string; value: string } | null {
  const trimmed = line.trim();
  const colonIdx = trimmed.indexOf(':');
  if (colonIdx === -1) return null;
  const key = trimmed.slice(0, colonIdx).trim();
  const value = trimmed.slice(colonIdx + 1).trim().replace(/^"|"$/g, '');
  return { key, value };
}

function extractEntryId(line: string, fallbackIndex: number, prefix = 'entry'): string | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('- id:') && !trimmed.startsWith('-     id:')) return null;
  const idMatch = trimmed.match(/id:\s*"([^"]+)"/) || trimmed.match(/id:\s*([^\s]+)/);
  return idMatch ? idMatch[1] : `${prefix}_${fallbackIndex}`;
}

// Helper parsing functions (defined before parseYAML since const functions aren't hoisted)
const parseMetadataLine = (line: string, metadata: any) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'format_version' || key === 'created_at' || key === 'source') {
    metadata[key] = value;
  } else if (key === 'patient') {
    if (!metadata.patient) metadata.patient = {};
  } else if (key === 'name' && metadata.patient) {
    metadata.patient.name = value;
  } else if (key === 'birth_date' && metadata.patient) {
    metadata.patient.birth_date = value;
  } else if (key === 'personal_number' && metadata.patient) {
    metadata.patient.personal_number = value;
  } else if (key === 'total_entries') {
    if (!metadata.export_info) metadata.export_info = {};
    metadata.export_info.total_entries = parseInt(value) || 0;
  }
};

const parseEntryLine = (line: string, entry: any, subsection: string | null = null) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'date') {
    entry.date = value;
  } else if (key === 'time') {
    entry.time = value;
  } else if (key === 'category') {
    entry.category = value;
  } else if (key === 'type') {
    entry.type = value;
  } else if (key === 'status') {
    entry.status = value;
  } else if (key === 'summary') {
    if (!entry.content) entry.content = {};
    entry.content.summary = value;
  } else if (key === 'details') {
    if (!entry.content) entry.content = {};
    entry.content.details = value;
  } else if (key === 'notes') {
    if (!entry.content) entry.content = {};
    entry.content.notes = value;
  } else if (key === 'tags') {
    entry.tags = value;
  } else if (subsection === 'provider') {
    entry.provider = entry.provider || {};
    if (key === 'name') entry.provider.name = value;
    if (key === 'region') entry.provider.region = value;
    if (key === 'location') entry.provider.location = value;
  } else if (subsection === 'responsible_person') {
    entry.responsible_person = entry.responsible_person || {};
    if (key === 'name') entry.responsible_person.name = value;
    if (key === 'role') entry.responsible_person.role = value;
  }
};

const parseJournalEntryLine = (line: string, entry: any) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'title') entry.title = value;
  else if (key === 'content') entry.content = value;
  else if (key === 'created_at') entry.created_at = value;
  else if (key === 'wellbeing_score') entry.wellbeing_score = parseInt(value) || undefined;
};

const parseChatEntryLine = (line: string, entry: any) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'summary') entry.summary = value;
  else if (key === 'timestamp') entry.timestamp = value;
  else if (key === 'transcript') entry.transcript = value;
  else if (key === 'aiDescription') entry.aiDescription = value;
};

const parseHealthActionLine = (line: string, entry: any) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'title') entry.title = value;
  else if (key === 'description') entry.description = value;
  else if (key === 'category') entry.category = value;
  else if (key === 'estimatedTime') entry.estimatedTime = value;
  else if (key === 'difficulty') entry.difficulty = value;
  else if (key === 'completed') entry.completed = value === 'true';
  else if (key === 'createdAt') entry.createdAt = value;
  else if (key === 'completedAt') entry.completedAt = value;
  else if (key === 'source') entry.source = value;
};

const parseHealthMemoryItemLine = (line: string, entry: any) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'id') entry.id = value;
  else if (key === 'category') entry.category = value;
  else if (key === 'label') entry.label = value;
  else if (key === 'detail') entry.detail = value;
  else if (key === 'source_type') entry.source_type = value;
  else if (key === 'confidence') entry.confidence = parseFloat(value) || 0.5;
  else if (key === 'certainty_level') entry.certainty_level = value;
  else if (key === 'status') entry.status = value;
  else if (key === 'observed_at') entry.observed_at = value;
  else if (key === 'updated_at') entry.updated_at = value;
  else if (key === 'last_used_at') entry.last_used_at = value;
  else if (key === 'evidence_refs') {
    try {
      const trimmed = line.trim();
      const colonIdx = trimmed.indexOf(':');
      const raw = trimmed.slice(colonIdx + 1).trim();
      let toParse = raw;
      if ((toParse.startsWith('"') && toParse.endsWith('"')) || (toParse.startsWith("'") && toParse.endsWith("'"))) {
        toParse = toParse.slice(1, -1).replace(/\\"/g, '"');
      }
      const parsed = JSON.parse(toParse);
      entry.evidence_refs = Array.isArray(parsed) ? parsed : [];
    } catch {
      entry.evidence_refs = [];
    }
  }
};

const parseNutritionEntryLine = (line: string, entry: any) => {
  const kv = parseKeyValueLine(line);
  if (!kv) return;
  const { key, value } = kv;

  if (key === 'name') entry.name = value;
  else if (key === 'description') entry.description = value;
  else if (key === 'timestamp') entry.timestamp = value;
  else if (key === 'source') entry.source = value;
  else if (key === 'notes') entry.notes = value;
  else if (key === 'calories') entry.calories = parseInt(value) || null;
  else if (key === 'carbohydrates') entry.carbohydrates = parseFloat(value) || null;
  else if (key === 'protein') entry.protein = parseFloat(value) || null;
  else if (key === 'fats') entry.fats = parseFloat(value) || null;
  else if (key === 'fiber') entry.fiber = parseFloat(value) || null;
  else if (key === 'sugar') entry.sugar = parseFloat(value) || null;
  else if (key === 'hydration') entry.hydration = parseFloat(value) || null;
  else if (key === 'glucoseLevel') entry.glucoseLevel = parseFloat(value) || null;
};

const parseHealthAssessmentLine = (
  line: string,
  assessments: any,
  subsection: string,
  setFormId: (formId: string | null) => void,
  getFormId: () => string | null
) => {
  const trimmed = line.trim();
  if (trimmed.includes(':')) {
    const [key, value] = trimmed.split(':', 2);
    const cleanKey = key.trim();
    const cleanValue = value.trim();

    if (subsection === 'responses') {
      // Format: formId: questionId: value
      if (!assessments.responses) assessments.responses = {};

      // Check if this is a formId (4 spaces indent)
      const indent = line.length - line.trimStart().length;
      if (indent === 4) {
        // This is a formId
        const formId = cleanKey;
        if (!assessments.responses[formId]) {
          assessments.responses[formId] = {};
        }
        setFormId(formId);
      } else if (indent === 6) {
        // This is a questionId: value pair
        const questionId = cleanKey;
        const numValue = parseInt(cleanValue);
        const formId = getFormId();
        if (!isNaN(numValue) && formId) {
          assessments.responses[formId][questionId] = numValue;
        }
      }
    } else if (subsection === 'results') {
      // Format: formId: JSON_STRING (may be quoted and escaped)
      if (!assessments.results) assessments.results = {};
      const indent = line.length - line.trimStart().length;

      // Only process non-empty lines that aren't comments
      if (!trimmed || trimmed.startsWith('#')) {
        return;
      }

      if (indent === 4 && trimmed.includes(':')) {
        // This is a formId: JSON_STRING
        const formId = cleanKey;
        // Extract the value part (everything after the colon)
        const colonIndex = trimmed.indexOf(':');
        if (colonIndex !== -1) {
          let jsonValue = trimmed.substring(colonIndex + 1).trim();

          // Handle quoted strings - need to properly extract and unescape
          if (jsonValue.startsWith('"')) {
            // Find the matching closing quote, handling escaped quotes
            let endQuoteIndex = -1;
            let i = 1; // Start after opening quote
            while (i < jsonValue.length) {
              if (jsonValue[i] === '"' && jsonValue[i - 1] !== '\\') {
                endQuoteIndex = i;
                break;
              }
              i++;
            }

            if (endQuoteIndex !== -1) {
              // Extract the quoted content
              jsonValue = jsonValue.substring(1, endQuoteIndex);
              // Unescape the string (handle \\, \", \n, etc.)
              // Do this carefully to avoid double-unescaping
              // eslint-disable-next-line no-control-regex
              jsonValue = jsonValue.replace(/\\\\/g, '\u0000') // Temporary marker for double backslash
                                   .replace(/\\"/g, '"')
                                   .replace(/\\'/g, "'")
                                   .replace(/\\n/g, '\n')
                                   .replace(/\\r/g, '\r')
                                   .replace(/\\t/g, '\t')
                                   // eslint-disable-next-line no-control-regex
                                   .replace(/\u0000/g, '\\'); // Restore double backslash
            }
          } else if (jsonValue.startsWith("'")) {
            // Handle single quotes similarly
            let endQuoteIndex = -1;
            let i = 1;
            while (i < jsonValue.length) {
              if (jsonValue[i] === "'" && jsonValue[i - 1] !== '\\') {
                endQuoteIndex = i;
                break;
              }
              i++;
            }

            if (endQuoteIndex !== -1) {
              jsonValue = jsonValue.substring(1, endQuoteIndex);
              // eslint-disable-next-line no-control-regex
              jsonValue = jsonValue.replace(/\\\\/g, '\u0000')
                                   .replace(/\\'/g, "'")
                                   .replace(/\\"/g, '"')
                                   .replace(/\\n/g, '\n')
                                   .replace(/\\r/g, '\r')
                                   .replace(/\\t/g, '\t')
                                   // eslint-disable-next-line no-control-regex
                                   .replace(/\u0000/g, '\\');
            }
          }

          try {
            const parsed = JSON.parse(jsonValue);
            assessments.results[formId] = parsed;
          } catch (e) {
            // If parsing fails, skip this result (don't store invalid data)
            console.warn(`[parseHealthAssessmentLine] Failed to parse JSON for form ${formId}, skipping. Error:`, (e as Error).message);
          }
        }
      }
    }
  }
};

// YAML Parser (simplified version of the original)
export const parseYAML = (text: string): EIRData => {
  try {
    const lines = text.split('\n');
    const result: any = {};
    let currentSection: string | null = null;
    let currentEntry: any = null;
    let entryIndex = -1;
    let currentSubsection: string | null = null;
    let currentArrayKey: string | null = null;
    let currentFormId: string | null = null; // For health_assessments parsing

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed || trimmed.startsWith('#')) continue;

      // const currentIndent = line.length - line.trimStart().length;

      if (trimmed === 'metadata:') {
        currentSection = 'metadata';
        result.metadata = {};
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'entries:') {
        currentSection = 'entries';
        result.entries = [];
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'journal_entries:') {
        currentSection = 'journal_entries';
        result.journal_entries = [];
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'chat_entries:') {
        currentSection = 'chat_entries';
        result.chat_entries = [];
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'health_actions:') {
        currentSection = 'health_actions';
        result.health_actions = [];
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'nutrition_entries:') {
        currentSection = 'nutrition_entries';
        result.nutrition_entries = [];
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'health_assessments:') {
        currentSection = 'health_assessments';
        result.health_assessments = {};
        currentEntry = null;
        entryIndex = -1;
      } else if (trimmed === 'health_memory_items:') {
        currentSection = 'health_memory_items';
        result.health_memory_items = [];
        currentEntry = null;
        entryIndex = -1;
      } else if (currentSection === 'metadata') {
        parseMetadataLine(line, result.metadata);
      } else if (currentSection === 'entries') {
        const entryId = extractEntryId(trimmed, entryIndex + 2);
        if (entryId) {
          entryIndex++;
          result.entries[entryIndex] = { id: entryId };
          currentEntry = result.entries[entryIndex];
          currentSubsection = null;
          currentArrayKey = null;
          currentEntry.provider = currentEntry.provider || {};
          currentEntry.responsible_person = currentEntry.responsible_person || {};
          currentEntry.content = currentEntry.content || {};
        } else if (currentEntry) {
          // Handle subsection switches
          if (trimmed === 'provider:') {
            currentSubsection = 'provider';
            currentArrayKey = null;
            currentEntry.provider = currentEntry.provider || {};
            continue;
          }
          if (trimmed === 'responsible_person:') {
            currentSubsection = 'responsible_person';
            currentArrayKey = null;
            currentEntry.responsible_person = currentEntry.responsible_person || {};
            continue;
          }
          if (trimmed === 'content:') {
            currentSubsection = 'content';
            currentArrayKey = null;
            currentEntry.content = currentEntry.content || {};
            continue;
          }
          if (trimmed === 'attachments:') {
            currentSubsection = null;
            currentArrayKey = 'attachments';
            currentEntry.attachments = currentEntry.attachments || [];
            continue;
          }
          if (trimmed === 'tags:') {
            currentSubsection = null;
            currentArrayKey = 'tags';
            currentEntry.tags = currentEntry.tags || [];
            continue;
          }
          if (trimmed === 'notes:' && currentSubsection === 'content') {
            currentArrayKey = 'notes';
            currentEntry.content.notes = currentEntry.content.notes || [];
            continue;
          }

          // Array items
          if (trimmed.startsWith('- ') && currentArrayKey) {
            const item = trimmed.replace(/^-\s+/, '').replace(/^"|"$/g, '');
            if (currentArrayKey === 'notes') {
              currentEntry.content = currentEntry.content || {};
              currentEntry.content.notes = currentEntry.content.notes || [];
              currentEntry.content.notes.push(item);
            } else {
              currentEntry[currentArrayKey] = currentEntry[currentArrayKey] || [];
              currentEntry[currentArrayKey].push(item);
            }
            continue;
          }

          parseEntryLine(line, currentEntry, currentSubsection);
        }
      } else if (currentSection === 'journal_entries') {
        const journalId = extractEntryId(trimmed, entryIndex + 2, 'journal');
        if (journalId) {
          entryIndex++;
          result.journal_entries[entryIndex] = { id: journalId };
          currentEntry = result.journal_entries[entryIndex];
        } else if (currentEntry) {
          parseJournalEntryLine(line, currentEntry);
        }
      } else if (currentSection === 'chat_entries') {
        const chatId = extractEntryId(trimmed, entryIndex + 2, 'chat');
        if (chatId) {
          entryIndex++;
          result.chat_entries[entryIndex] = { id: chatId };
          currentEntry = result.chat_entries[entryIndex];
        } else if (currentEntry) {
          parseChatEntryLine(line, currentEntry);
        }
      } else if (currentSection === 'health_actions') {
        const actionId = extractEntryId(trimmed, entryIndex + 2, 'action');
        if (actionId) {
          entryIndex++;
          result.health_actions[entryIndex] = { id: actionId, benefits: [], instructions: [], tips: [] };
          currentEntry = result.health_actions[entryIndex];
          currentArrayKey = null;
        } else if (currentEntry) {
          if (trimmed === 'benefits:') {
            currentArrayKey = 'benefits';
            currentEntry.benefits = currentEntry.benefits || [];
            continue;
          }
          if (trimmed === 'instructions:') {
            currentArrayKey = 'instructions';
            currentEntry.instructions = currentEntry.instructions || [];
            continue;
          }
          if (trimmed === 'tips:') {
            currentArrayKey = 'tips';
            currentEntry.tips = currentEntry.tips || [];
            continue;
          }
          if (trimmed.startsWith('- ') && currentArrayKey) {
            const item = trimmed.replace(/^-\s+/, '').replace(/^"|"$/g, '');
            currentEntry[currentArrayKey] = currentEntry[currentArrayKey] || [];
            currentEntry[currentArrayKey].push(item);
            continue;
          }
          parseHealthActionLine(line, currentEntry);
        }
      } else if (currentSection === 'nutrition_entries') {
        const nutritionId = extractEntryId(trimmed, entryIndex + 2, 'nutrition');
        if (nutritionId) {
          entryIndex++;
          result.nutrition_entries[entryIndex] = { id: nutritionId };
          currentEntry = result.nutrition_entries[entryIndex];
        } else if (currentEntry) {
          parseNutritionEntryLine(line, currentEntry);
        }
      } else if (currentSection === 'health_memory_items') {
        const memId = extractEntryId(trimmed, entryIndex + 2, 'mem');
        if (memId) {
          entryIndex++;
          result.health_memory_items[entryIndex] = { id: memId, evidence_refs: [] };
          currentEntry = result.health_memory_items[entryIndex];
        } else if (currentEntry) {
          parseHealthMemoryItemLine(line, currentEntry);
        }
      } else if (currentSection === 'health_assessments') {
        if (trimmed === 'responses:') {
          currentSubsection = 'responses';
          result.health_assessments.responses = {};
          currentFormId = null;
          console.log('[parseYAML] Entered responses subsection');
          continue;
        } else if (trimmed === 'results:') {
          currentSubsection = 'results';
          result.health_assessments.results = {};
          currentFormId = null;
          continue;
        } else if (currentSubsection === 'responses' || currentSubsection === 'results') {
          // eslint-disable-next-line no-loop-func
          parseHealthAssessmentLine(line, result.health_assessments, currentSubsection, (formId: string | null) => {
            currentFormId = formId;
          // eslint-disable-next-line no-loop-func
          }, () => currentFormId);
        }
      }
    }

    // Clean up the data
    if (result.entries) {
      result.entries = result.entries.filter((entry: any) => entry && entry.id);
    }

    // Clean up extended sections
    if (result.journal_entries) {
      result.journal_entries = result.journal_entries.filter((entry: any) => entry && entry.id);
    }
    if (result.chat_entries) {
      result.chat_entries = result.chat_entries.filter((entry: any) => entry && entry.id);
    }
    if (result.health_actions) {
      result.health_actions = result.health_actions.filter((entry: any) => entry && entry.id);
    }
    if (result.nutrition_entries) {
      result.nutrition_entries = result.nutrition_entries.filter((entry: any) => entry && entry.id);
    }
    if (result.health_memory_items) {
      result.health_memory_items = result.health_memory_items.filter((entry: any) => entry && entry.id && entry.label);
    }

    return result as EIRData;
  } catch (error) {
    console.error('YAML parsing error:', error);
    throw new Error('Invalid YAML format');
  }
};
